/**
 * Reset Password — consumes the token from a forgot-password email link
 * (?uid=...&token=...) and sets a new password. Public route, no auth
 * required (the token itself is the credential for this one action).
 */

import React, { useState, FormEvent } from "react";
import axios, { AxiosError } from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { PasswordField } from "./PasswordChange";

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid") || "";
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const missingParams = !uid || !token;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setServerErrors([]);

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match — check both fields carefully.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post<{ success: boolean; message: string }>(
        "/api/auth/reset-password",
        { uid, token, newPassword }
      );
      if (response.data.success) {
        setSuccess("Password reset! Redirecting to login...");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: string; errors?: string[] }>;
      const msg = axiosError.response?.data?.error || "Password reset failed. The link may have expired — request a new one.";
      const errs = axiosError.response?.data?.errors;
      setError(msg);
      if (errs?.length) setServerErrors(errs);
    } finally {
      setLoading(false);
    }
  };

  if (missingParams) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h2>Reset Password</h2>
          <div className="error-message">This reset link is invalid or incomplete.</div>
          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <Link to="/forgot-password" style={{ fontSize: "14px", color: "#15403a" }}>Request a new link</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Reset Password</h2>

        <form onSubmit={handleSubmit}>
          <PasswordField
            id="newPassword"
            label="New Password:"
            value={newPassword}
            onChange={setNewPassword}
            placeholder="Enter new password"
          />
          <PasswordField
            id="confirmPassword"
            label="Confirm New Password:"
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="Type new password again"
          />

          {error && (
            <div className="error-message">
              {error}
              {serverErrors.length > 0 && (
                <ul style={{ marginTop: "6px", paddingLeft: "18px", fontSize: "13px" }}>
                  {serverErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}

          {success && (
            <div style={{ backgroundColor: "#d4edda", color: "#155724", padding: "10px", borderRadius: "5px", marginBottom: "10px" }}>
              {success}
            </div>
          )}

          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
